import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { API } from "@/lib/api";

function getSessionId() {
  let sid = localStorage.getItem("at_chat_sid");
  if (!sid) {
    sid = "sid-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem("at_chat_sid", sid);
  }
  return sid;
}

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi, I'm Otto — Adtrax Logistics' dispatch assistant. Ask me how we dispatch, our fees, onboarding, or how to sign your truck up." },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  const sid = useRef(getSessionId());

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setInput("");
    setBusy(true);

    try {
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ session_id: sid.current, message: text }),
      });
      if (!res.body) throw new Error("No stream body");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const data = line.slice(6);
          if (data === "[DONE]") continue;
          if (data.startsWith("[error]")) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: "Sorry, I hit an error. Please try again or reach dispatch at dispatch@ironhaul.co." };
              return copy;
            });
            continue;
          }
          setMessages((m) => {
            const copy = [...m];
            copy[copy.length - 1] = {
              role: "assistant",
              content: (copy[copy.length - 1].content || "") + data,
            };
            return copy;
          });
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Network error. Please try again." };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 bg-[#FF4500] text-white p-4 sharp-btn shadow-lg hover:bg-black flex items-center gap-2"
          data-testid="chatbot-open-button"
        >
          <MessageCircle size={20}/>
          <span className="font-mono-label text-xs">ASK OTTO</span>
        </button>
      )}

      {open && (
        <div className="fixed bottom-6 right-6 z-50 w-[min(380px,calc(100vw-2rem))] h-[560px] max-h-[80vh] bg-white border border-black flex flex-col shadow-2xl" data-testid="chatbot-window">
          <div className="bg-black text-white px-5 py-4 flex items-center justify-between">
            <div>
              <p className="font-mono-label text-[10px] text-[#FF4500]">ADTRAX DISPATCH</p>
              <p className="font-display font-semibold">Otto</p>
            </div>
            <button onClick={() => setOpen(false)} data-testid="chatbot-close-button" aria-label="Close chat">
              <X size={20}/>
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 scrollbar-thin bg-[#F8F9FA]" data-testid="chatbot-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-black text-white" : "bg-white border border-black/10"}`}>
                  {m.content || <span className="inline-flex items-center gap-2 text-black/50"><Loader2 size={14} className="animate-spin"/> thinking…</span>}
                </div>
              </div>
            ))}
          </div>

          <div className="border-t border-black/10 p-3 flex items-end gap-2 bg-white">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              rows={1}
              placeholder="Type your question…"
              className="flex-1 resize-none text-sm border border-black/15 px-3 py-2 focus:outline-none focus:border-[#FF4500]"
              data-testid="chatbot-input"
            />
            <button
              onClick={send}
              disabled={busy || !input.trim()}
              className="sharp-btn bg-[#FF4500] text-white p-3 hover:bg-black disabled:opacity-50"
              data-testid="chatbot-send-button"
              aria-label="Send"
            >
              <Send size={16}/>
            </button>
          </div>
        </div>
      )}
    </>
  );
}
